import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Shield, ArrowLeft } from 'lucide-react';
import api from '@/lib/api';

export default function LoginPage() {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [seeding, setSeeding] = useState(false);

  // 2FA state
  const [needs2FA, setNeeds2FA] = useState(false);
  const [totpCode, setTotpCode] = useState('');
  const totpRef = useRef(null);

  useEffect(() => {
    const seedDb = async () => {
      setSeeding(true);
      try { await api.post('/seed'); } catch { /* seed endpoint is idempotent — ignore errors */ }
      setSeeding(false);
    };
    seedDb();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (needs2FA) {
        // Second step: send email + password + totp_code
        await login(email, password, totpCode);
        toast.success('Welcome to NYCD Portal');
      } else {
        // First step: send email + password
        const result = await login(email, password);
        if (result?.requires_2fa) {
          setNeeds2FA(true);
          setTotpCode('');
          setTimeout(() => totpRef.current?.focus(), 100);
        } else {
          toast.success('Welcome to NYCD Portal');
        }
      }
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Login failed';
      toast.error(msg);
      if (needs2FA && msg.includes('Invalid authenticator')) {
        setTotpCode('');
      }
    }
    setLoading(false);
  };

  const goBack = () => {
    setNeeds2FA(false);
    setTotpCode('');
    setPassword('');
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="login-page">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <h1 className="font-heading font-black text-3xl tracking-tight">
            NYCD<span className="text-primary ml-1">Portal</span>
          </h1>
        </div>

        <form
          onSubmit={handleSubmit}
          className="border border-border bg-card rounded-md p-6 space-y-4"
          data-testid="login-form"
        >
          {!needs2FA ? (
            <>
              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-xs font-semibold text-muted-foreground">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="user@nycdgroup.com"
                  className="bg-background border-border"
                  data-testid="login-email-input"
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="password" className="text-xs font-semibold text-muted-foreground">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                  className="bg-background border-border"
                  data-testid="login-password-input"
                  required
                />
              </div>
            </>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-primary">
                <Shield className="h-5 w-5" />
                <p className="text-sm font-semibold">Two-Factor Authentication</p>
              </div>
              <p className="text-xs text-muted-foreground">
                Enter the 6-digit code from your authenticator app
              </p>
              <Input
                ref={totpRef}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
                placeholder="000000"
                className="bg-background border-border text-center text-2xl tracking-[0.5em] font-mono"
                data-testid="login-totp-input"
                autoComplete="one-time-code"
                required
              />
              <button
                type="button"
                onClick={goBack}
                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
                data-testid="login-back-btn"
              >
                <ArrowLeft className="h-3 w-3" /> Back to login
              </button>
            </div>
          )}

          <Button
            type="submit"
            className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
            disabled={loading || seeding || (needs2FA && totpCode.length !== 6)}
            data-testid="login-submit-button"
          >
            {seeding ? 'Initializing...' : loading ? 'Signing in...' : needs2FA ? 'Verify & Sign In' : 'Sign In'}
          </Button>
        </form>
      </div>
    </div>
  );
}
